import { cn } from '../lib/cn';
import { renderAgentMarkdown } from '../lib/markdown';
import { BubbleTailUser, BubbleTailBot, TickIcon } from './icons';
import type { ChatMessage } from './useChatState';
import { MessageReplyButtons } from './MessageReplyButtons';
import { MessageMedia } from './MessageMedia';

type TickStatus = 'sent' | 'delivered' | 'read';

interface MessageBubbleProps {
  message: ChatMessage;
  brandingColor: string;
  bubbleStyle?: 'rounded' | 'classic';
  /** Whether this is the last message in a group from the same sender */
  isLastInGroup?: boolean;
  /** Tick status for user messages */
  tickStatus?: TickStatus;
  /**
   * When set, the bubble renders the message's `replyButtons` as a
   * WhatsApp-style stack under the body, sharing the same `max-w-[76%]`
   * container so both are exactly the same rendered width. Absent =
   * do not render (either the message has no buttons, or the visitor
   * already responded — the caller decides via this prop, since it
   * needs access to the surrounding message list to know).
   */
  onReplyButtonSelect?: (title: string) => void;
}

function formatTimestamp(ts?: number): string {
  if (!ts) return '';
  const d = new Date(ts);
  return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
}

export function MessageBubble({
  message,
  brandingColor,
  bubbleStyle = 'rounded',
  isLastInGroup = true,
  tickStatus,
  onReplyButtonSelect,
}: MessageBubbleProps) {
  const { role } = message;
  const radius = bubbleStyle === 'rounded' ? 'rounded-[18px]' : 'rounded-[9px]';
  const time = formatTimestamp(message.timestamp);

  if (role === 'system') {
    return (
      <div class="flex justify-center px-4 mb-2">
        <span
          class={cn(
            'text-[11px] text-w-secondary italic text-center',
            'px-3 py-1.5 rounded-full',
            'bg-w-bb-bg/60',
          )}
        >
          {message.text}
        </span>
      </div>
    );
  }

  if (role === 'user') {
    return (
      <div
        class={cn(
          'flex justify-end px-3 animate-msg-in',
          isLastInGroup ? 'mb-[10px]' : 'mb-[3px]',
        )}
      >
        <div class="relative max-w-[76%]">
          <div
            class={cn(
              'px-[10px] pt-[8px] pb-[8px] pl-[12px]',
              'text-w-ub-fg text-[14.5px] leading-[1.42]',
              'shadow-[0_1px_1px_rgba(0,0,0,0.08)]',
              'overflow-hidden',
              radius,
              isLastInGroup &&
                (bubbleStyle === 'rounded'
                  ? 'rounded-br-[10px]'
                  : 'rounded-br-[5px]'),
            )}
            style={
              {
                background: 'var(--w-ub-bg)',
                '--mirlo-brand': brandingColor,
              } as Record<string, string>
            }
          >
            {message.media && (
              <div class={cn(message.text ? 'mb-[6px]' : 'mb-0')}>
                <MessageMedia attachment={message.media} />
              </div>
            )}
            {message.text}
            {/* Inline timestamp + ticks — use non-breaking padding to ensure
                the bubble expands to contain the floated meta even on very
                short messages ("ok", "hola") */}
            <span class="float-right ml-[9px] mt-[9px] -mb-[4px] relative top-[4px] flex items-center gap-[3px]">
              <span class="text-[11px] text-w-meta-user">{time}</span>
              {tickStatus && (
                <TickIcon
                  double={tickStatus !== 'sent'}
                  read={tickStatus === 'read'}
                  class="w-[15px] h-[11px]"
                />
              )}
            </span>
          </div>
          {isLastInGroup && (
            <BubbleTailUser
              fill="var(--w-ub-bg)"
              class="absolute bottom-[-0.5px] right-[-6.5px]"
            />
          )}
        </div>
      </div>
    );
  }

  // Agent message — render markdown
  const md = renderAgentMarkdown(message.text);
  const showReplyButtons =
    Array.isArray(message.replyButtons) &&
    message.replyButtons.length > 0 &&
    typeof onReplyButtonSelect === 'function';
  return (
    <div
      class={cn(
        'flex justify-start px-3 animate-msg-in',
        isLastInGroup ? 'mb-[10px]' : 'mb-[3px]',
      )}
    >
      {/* Shared wrapper: `max-w-[76%]` bounds both the bubble AND the
          reply-button stack below it. As a flex column with `w-fit`,
          the wrapper shrinks to the widest child so the buttons and
          bubble share the same rendered width — matches WhatsApp's
          "buttons look like they belong to the message above them"
          visual. The old separate `<MessageReplyButtons>` container had
          its own `max-w-[76%]` and blew up in standalone / wide-viewport
          mode, where 76% of the viewport was much wider than the
          content-sized bubble. */}
      <div class="max-w-[76%] w-fit flex flex-col gap-[4px]">
        <div class="relative">
          <div
            class={cn(
              'px-[10px] pt-[8px] pb-[8px] pl-[12px]',
              'bg-w-bb-bg text-w-bb-fg text-[14.5px] leading-[1.42]',
              'shadow-[0_1px_1px_rgba(0,0,0,0.08)]',
              'overflow-hidden',
              radius,
              isLastInGroup &&
                (bubbleStyle === 'rounded'
                  ? 'rounded-bl-[10px]'
                  : 'rounded-bl-[5px]'),
            )}
          >
            {/* Interactive list header — a bold line above the body, the
                same weight WhatsApp gives it. Lists are the only payload
                that carries one. */}
            {message.header && (
              <div class="font-semibold mb-[3px]">{message.header}</div>
            )}
            {/* Attachment above the caption: the caption describes the file,
                so it reads in that order — same as WhatsApp. */}
            {message.media && (
              <div class={cn(message.text ? 'mb-[6px]' : 'mb-0')}>
                <MessageMedia attachment={message.media} />
              </div>
            )}
            {/* Prose resets live on the markdown wrapper, not on the bubble:
                they are descendant selectors, so on the bubble they also hit
                the attachment card's anchor and painted the filename as a
                blue underlined link. */}
            {message.text && (
              <span
                class={cn(
                  // A URL is one unbreakable word: without this it runs past
                  // the bubble, which clips it (`overflow-hidden` above).
                  'break-words',
                  '[&_strong]:font-semibold [&_em]:italic',
                  '[&_code]:bg-black/5 [&_code]:px-1 [&_code]:rounded [&_code]:text-xs [&_code]:font-mono',
                  '[&_a]:text-blue-600 [&_a]:underline',
                  '[&_p]:m-0 [&_ul]:m-0 [&_ol]:m-0 [&_li]:ml-4',
                )}
                dangerouslySetInnerHTML={md}
              />
            )}
            {/* Optional footer text on interactive-button messages —
                rendered below the body, subtle style. Matches the visual
                weight WhatsApp gives the interactive footer. */}
            {message.footer && (
              <div class="mt-[6px] pt-[4px] border-t border-w-secondary/15 text-[11.5px] text-w-secondary italic">
                {message.footer}
              </div>
            )}
            {/* Inline timestamp */}
            <span class="float-right ml-[9px] mt-[9px] -mb-[4px] relative top-[4px]">
              <span class="text-[11px] text-w-meta-bot">{time}</span>
            </span>
          </div>
          {isLastInGroup && (
            <BubbleTailBot
              // --w-bb-bg is now stored as space-separated RGB channels so
              // Tailwind can synthesize opacity utilities. That means the raw
              // var() is not a valid SVG fill on its own — wrap in rgb().
              fill="rgb(var(--w-bb-bg))"
              class="absolute bottom-[-0.5px] left-[-6.5px]"
            />
          )}
        </div>
        {showReplyButtons && (
          <MessageReplyButtons
            buttons={message.replyButtons!}
            brandingColor={brandingColor}
            onSelect={onReplyButtonSelect!}
          />
        )}
      </div>
    </div>
  );
}
